import type { Task } from "@app/core";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useCalendarsStore, useTasksStore, useThemeStore, useTopicsStore } from "../../stores/index.js";
import {
  blockTintFromDiff,
  childTint,
  EVENT_DEFAULT_HEX,
  PRIORITY_HEX,
  type BlockTint,
} from "../task-colors.js";
import { resolvedCalendarColor, resolvedTaskContextColor, resolvedTopicColors } from "../entity-colors.js";
import { useEffectivePriority } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { sameTaskRenderSignature } from "./build-days.js";

export interface SubtaskProgress {
  done: number;
  total: number;
}

export interface CalendarRenderIndex {
  blockStyle: { tint: BlockTint; gradient: boolean };
  contextColorOf: (task: { calendar_id: string | null; topic_ids: string[]; is_event?: boolean }) => string | null;
  /** All colors of the task's topics, in order (mobile gradient look). */
  topicColorsOf: (task: { topic_ids: string[] }) => string[];
  /** The task's calendar color (mobile gradient tail). */
  calendarColorOf: (task: { calendar_id: string | null }) => string | null;
}

const EMPTY_PROGRESS: SubtaskProgress = { done: 0, total: 0 };
const EMPTY_CHILDREN: Task[] = [];
const EMPTY_CHILD_INFO: { children: Task[]; progress: SubtaskProgress } = { children: EMPTY_CHILDREN, progress: EMPTY_PROGRESS };
const CalendarRenderIndexContext = createContext<CalendarRenderIndex | null>(null);

export function CalendarRenderIndexProvider({ children }: { children: ReactNode }) {
  const calendars = useCalendarsStore((s) => s.calendars);
  const topics = useTopicsStore((s) => s.topics);
  const theme = useThemeStore((s) => s.config);

  const value = useMemo(() => {
    const blockStyle = { tint: blockTintFromDiff(theme.blockTintDiff), gradient: theme.blockGradient };

    return {
      blockStyle,
      contextColorOf: (task: { calendar_id: string | null; topic_ids: string[]; is_event?: boolean }) =>
        resolvedTaskContextColor(theme, topics, calendars, task),
      topicColorsOf: (task: { topic_ids: string[] }) => resolvedTopicColors(theme, topics, task.topic_ids),
      calendarColorOf: (task: { calendar_id: string | null }) =>
        resolvedCalendarColor(theme, calendars, task.calendar_id),
    };
  }, [calendars, theme, topics]);

  return <CalendarRenderIndexContext.Provider value={value}>{children}</CalendarRenderIndexContext.Provider>;
}

export function useCalendarRenderIndex(): CalendarRenderIndex {
  const index = useContext(CalendarRenderIndexContext);
  if (!index) throw new Error("useCalendarRenderIndex must be used inside CalendarRenderIndexProvider");
  return index;
}

export function useCalendarChildInfo(parentId: string): { children: Task[]; progress: SubtaskProgress } {
  const [info, setInfo] = useState(() => buildChildInfo(parentId, useTasksStore.getState().tasks));

  useEffect(() => {
    const update = () => {
      const next = buildChildInfo(parentId, useTasksStore.getState().tasks);
      setInfo((prev) => sameChildInfo(prev, next) ? prev : next);
    };
    update();
    return useTasksStore.subscribe(update);
  }, [parentId]);

  return info;
}

export function useCalendarProgress(parentId: string): SubtaskProgress {
  return useCalendarChildInfo(parentId).progress;
}

export function useCalendarInheritedTask(task: Task): Task {
  const direct = useMemo(() => inheritedTaskFrom(task, useTasksStore.getState().tasks), [task]);
  const [inherited, setInherited] = useState(direct);

  useEffect(() => {
    const update = () => {
      const next = inheritedTaskFrom(task, useTasksStore.getState().tasks);
      setInherited((prev) => sameInheritedTask(prev, next) ? prev : next);
    };
    update();
    return useTasksStore.subscribe(update);
  }, [direct, task]);

  const base = sameInheritedTask(inherited, direct) ? inherited : direct;
  // Overlay the LIVE priority (matrix diagonal + deadline-derived urgency) so a
  // block's priority color tracks the deadline WITHOUT the task being re-saved.
  // No-op for off-scope / unplaced tasks (priority unchanged → same reference).
  const resolvePriority = useEffectivePriority();
  return useMemo(() => {
    const p = resolvePriority(base);
    return p === base.priority ? base : { ...base, priority: p };
  }, [base, resolvePriority]);
}

let childIndexTasks: ReadonlyArray<Task> | null = null;
let childIndex: Map<string, { children: Task[]; progress: SubtaskProgress }> = new Map();

function buildChildInfo(parentId: string, tasks: ReadonlyArray<Task>): { children: Task[]; progress: SubtaskProgress } {
  return getChildIndex(tasks).get(parentId) ?? EMPTY_CHILD_INFO;
}

function getChildIndex(tasks: ReadonlyArray<Task>): Map<string, { children: Task[]; progress: SubtaskProgress }> {
  if (childIndexTasks === tasks) return childIndex;
  const grouped = new Map<string, { children: Task[]; done: number }>();
  for (const task of tasks) {
    if (task.virtual || task.deleted_at || !task.subtask_parent_id) continue;
    const entry = grouped.get(task.subtask_parent_id) ?? { children: [], done: 0 };
    entry.children.push(task);
    if (task.status === "done") entry.done++;
    grouped.set(task.subtask_parent_id, entry);
  }
  const next = new Map<string, { children: Task[]; progress: SubtaskProgress }>();
  for (const [parentId, entry] of grouped) {
    entry.children.sort((a, b) => (a.subtask_order - b.subtask_order) || a.created_at.localeCompare(b.created_at));
    next.set(parentId, {
      children: entry.children,
      progress: { done: entry.done, total: entry.children.length },
    });
  }
  childIndexTasks = tasks;
  childIndex = next;
  return childIndex;
}

function sameChildInfo(
  a: { children: Task[]; progress: SubtaskProgress },
  b: { children: Task[]; progress: SubtaskProgress },
): boolean {
  if (a.progress.done !== b.progress.done || a.progress.total !== b.progress.total) return false;
  if (a.children.length !== b.children.length) return false;
  for (let i = 0; i < a.children.length; i++) {
    const x = a.children[i]!;
    const y = b.children[i]!;
    if (x !== y && !sameTaskRenderSignature(x, y)) return false;
  }
  return true;
}

// One id map per tasks-array ref (same caching contract as the child index):
// every mounted block's subscriber runs on a store change, so a per-call map
// build would re-scan the whole dataset once per visible detached subtask.
let taskByIdTasks: ReadonlyArray<Task> | null = null;
let taskById: Map<string, Task> = new Map();

function getTaskById(tasks: ReadonlyArray<Task>): Map<string, Task> {
  if (taskByIdTasks === tasks) return taskById;
  taskById = new Map(tasks.map((t) => [t.id, t]));
  taskByIdTasks = tasks;
  return taskById;
}

function inheritedTaskFrom(task: Task, tasks: ReadonlyArray<Task>): Task {
  if (task.custom_color || !task.subtask_parent_id) return task;
  const color = effectiveColorIndexed(task, getTaskById(tasks));
  return color ? { ...task, custom_color: color } : task;
}

function effectiveColorIndexed(
  task: Pick<Task, "custom_color" | "subtask_parent_id" | "priority" | "is_event">,
  byId: Map<string, Task>,
  depth = 0,
): string | null {
  if (task.custom_color) return task.custom_color;
  if (task.subtask_parent_id && depth < 20) {
    const parent = byId.get(task.subtask_parent_id);
    if (parent) {
      const color = effectiveColorIndexed(parent, byId, depth + 1);
      if (color) return childTint(color);
    }
  }
  const priority = PRIORITY_HEX[task.priority];
  if (priority) return priority;
  return task.is_event ? EVENT_DEFAULT_HEX : null;
}

function sameInheritedTask(a: Task, b: Task): boolean {
  return a === b || (
    a.id === b.id &&
    a.custom_color === b.custom_color &&
    a.priority === b.priority &&
    a.status === b.status &&
    a.updated_at === b.updated_at
  );
}
